class PurchaseInfoDrawer extends HTMLElement {
  constructor() {
    super()

    this.overlay = this.querySelector('.cart-drawer__overlay')
    this.inner = this.querySelector('.drawer__inner')
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'))
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'))
    this.isOpen = false

    this.close = this.close.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)
    this.onTabClick = this.onTabClick.bind(this)
    this.onTabKeydown = this.onTabKeydown.bind(this)

    if (this.overlay) this.overlay.addEventListener('click', this.close)

    const closeBtn = this.querySelector('[data-purchase-info-close]')
    if (closeBtn) closeBtn.addEventListener('click', this.close)

    this.addEventListener('keyup', this.onKeyUp)

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick)
      tab.addEventListener('keydown', this.onTabKeydown)
    })

    this.setAttribute('aria-hidden', 'true')
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this)
    }
  }

  onKeyUp(event) {
    if (!this.isOpen) return
    if (event.code === 'Escape') this.close()
  }

  onTabClick(event) {
    const index = Number(event.currentTarget.dataset.tabIndex)
    if (!Number.isNaN(index)) this.selectTab(index)
  }

  onTabKeydown(event) {
    if (!this.tabs.length) return

    const currentIndex = this.tabs.indexOf(event.currentTarget)
    if (currentIndex < 0) return

    let nextIndex = currentIndex

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.tabs.length
      event.preventDefault()
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length
      event.preventDefault()
    } else if (event.key === 'Home') {
      nextIndex = 0
      event.preventDefault()
    } else if (event.key === 'End') {
      nextIndex = this.tabs.length - 1
      event.preventDefault()
    } else {
      return
    }

    this.selectTab(nextIndex)
    this.tabs[nextIndex].focus()
  }

  selectTab(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index
      tab.classList.toggle('is-active', selected)
      tab.setAttribute('aria-selected', selected ? 'true' : 'false')
      tab.tabIndex = selected ? 0 : -1
    })

    this.panels.forEach((panel, i) => {
      const selected = i === index
      panel.classList.toggle('is-active', selected)
      if (selected) {
        panel.removeAttribute('hidden')
      } else {
        panel.setAttribute('hidden', '')
      }
    })
  }

  selectTabByHandle(handle) {
    if (!handle) return false
    const normalized = handle.replace(/^#/, '').toLowerCase()
    const tab = this.tabs.find((el) => (el.dataset.tabHandle || '').toLowerCase() === normalized)
    if (!tab) return false
    const index = Number(tab.dataset.tabIndex)
    if (!Number.isNaN(index)) this.selectTab(index)
    return true
  }

  open(triggeredBy, handle) {
    if (triggeredBy) this.activeElement = triggeredBy
    if (handle) this.selectTabByHandle(handle)

    if (this.isOpen) return
    this.isOpen = true
    this.setAttribute('aria-hidden', 'false')

    setTimeout(() => {
      this.classList.add('animate', 'active')
    })

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.isOpen) return
        const containerToTrapFocusOn = this.inner || this
        const focusElement =
          this.querySelector('.purchase-info-drawer__tab.is-active') ||
          this.querySelector('.drawer__close') ||
          containerToTrapFocusOn
        if (typeof trapFocus === 'function') trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true }
    )

    if (typeof lockDrawerScroll === 'function') lockDrawerScroll()
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.classList.remove('active')
    this.setAttribute('aria-hidden', 'true')
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement)
    if (typeof unlockDrawerScroll === 'function') unlockDrawerScroll()
  }
}

class PurchaseInfo extends HTMLElement {
  constructor() {
    super()

    this.slides = Array.from(this.querySelectorAll('.purchase-info__slide'))
    this.dots = Array.from(this.querySelectorAll('.purchase-info__dot'))
    this.track = this.querySelector('.purchase-info__slides')
    this.currentIndex = 0
    this.autoplayTimer = null
    this.isPaused = false
    this.isAnimating = false
    this.initialized = false
    this.cleanupTimer = null

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.prefersReducedMotion
    this.autoplaySpeed = Math.max(1, Number(this.dataset.autoplaySpeed) || 6) * 1000
    this.transitionSpeed = this.prefersReducedMotion
      ? 0
      : Math.max(0, Number(this.dataset.transitionSpeed) || 500)

    this.drag = {
      active: false,
      startX: 0,
      startY: 0,
      deltaX: 0,
      pointerId: null,
      moved: false,
    }

    this.goTo = this.goTo.bind(this)
    this.next = this.next.bind(this)
    this.prev = this.prev.bind(this)
    this.pause = this.pause.bind(this)
    this.resume = this.resume.bind(this)
    this.onCarouselClick = this.onCarouselClick.bind(this)
    this.onHashChange = this.onHashChange.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    this.onVisibilityChange = this.onVisibilityChange.bind(this)
  }

  connectedCallback() {
    if (this.initialized) return
    this.initialized = true

    if (this.prefersReducedMotion) {
      this.style.setProperty('--purchase-info-transition', '0ms')
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number(dot.dataset.slideTo)
        if (Number.isNaN(index)) return
        const direction = index < this.currentIndex ? 'prev' : 'next'
        this.goTo(index, direction)
      })
    })

    this.addEventListener('click', this.onCarouselClick)
    this.addEventListener('mouseenter', this.pause)
    this.addEventListener('mouseleave', this.resume)
    this.addEventListener('focusin', this.pause)
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume()
    })

    if (this.track && this.slides.length > 1) {
      this.track.addEventListener('pointerdown', this.onPointerDown)
    } else if (this.track) {
      this.track.style.cursor = 'default'
    }

    window.addEventListener('hashchange', this.onHashChange)
    document.addEventListener('visibilitychange', this.onVisibilityChange)

    if (this.slides.length > 1 && this.autoplayEnabled) {
      this.startAutoplay()
    }

    requestAnimationFrame(() => this.openFromHash(window.location.hash, null))
  }

  disconnectedCallback() {
    this.stopAutoplay()
    this.teardownDragListeners()
    if (this.cleanupTimer) clearTimeout(this.cleanupTimer)
    window.removeEventListener('hashchange', this.onHashChange)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.initialized = false
  }

  get drawer() {
    const drawerId = this.dataset.drawer
    return drawerId ? document.getElementById(drawerId) : null
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.pause()
    } else if (!this.matches(':hover') && !this.contains(document.activeElement)) {
      this.resume()
    }
  }

  onCarouselClick(event) {
    if (this.drag.moved) {
      event.preventDefault()
      event.stopPropagation()
      this.drag.moved = false
      return
    }

    const link = event.target.closest('a[href]')
    if (!link || !this.contains(link)) return

    const hash = this.getLinkHash(link)
    if (!hash) return

    if (this.openFromHash(hash, link)) {
      event.preventDefault()
    }
  }

  getLinkHash(link) {
    const href = link.getAttribute('href')
    if (!href || href === '#') return null

    if (href.startsWith('#')) return href

    try {
      const url = new URL(href, window.location.origin)
      if (url.origin !== window.location.origin) return null
      if (url.pathname !== window.location.pathname) return null
      return url.hash || null
    } catch (e) {
      return null
    }
  }

  onHashChange() {
    this.openFromHash(window.location.hash, null)
  }

  openFromHash(hash, triggeredBy) {
    if (!hash || hash === '#') return false
    const drawer = this.drawer
    if (!drawer || typeof drawer.open !== 'function') return false

    const handle = hash.replace(/^#/, '')
    if (!drawer.selectTabByHandle || !drawer.selectTabByHandle(handle)) return false

    drawer.open(triggeredBy || this)
    return true
  }

  onPointerDown(event) {
    if (this.slides.length < 2 || this.isAnimating) return
    if (event.target.closest('.purchase-info__dot')) return
    if (event.button != null && event.button !== 0) return

    this.drag.active = true
    this.drag.moved = false
    this.drag.startX = event.clientX
    this.drag.startY = event.clientY
    this.drag.deltaX = 0
    this.drag.pointerId = event.pointerId

    this.pause()
    this.track.classList.add('is-dragging')

    window.addEventListener('pointermove', this.onPointerMove, { passive: true })
    window.addEventListener('pointerup', this.onPointerUp)
    window.addEventListener('pointercancel', this.onPointerUp)
  }

  onPointerMove(event) {
    if (!this.drag.active) return

    this.drag.deltaX = event.clientX - this.drag.startX
    const deltaY = event.clientY - this.drag.startY

    if (!this.drag.moved && Math.abs(this.drag.deltaX) > 8 && Math.abs(this.drag.deltaX) > Math.abs(deltaY)) {
      this.drag.moved = true
      try {
        this.track.setPointerCapture(this.drag.pointerId)
      } catch (e) {
        // ignore
      }
    }
  }

  onPointerUp() {
    if (!this.drag.active) return

    const threshold = Math.min(60, (this.track?.offsetWidth || 200) * 0.2)
    const deltaX = this.drag.deltaX
    const didSwipe = this.drag.moved && Math.abs(deltaX) >= threshold

    this.teardownDragListeners()
    this.track.classList.remove('is-dragging')
    this.drag.active = false

    if (didSwipe) {
      if (deltaX < 0) this.next()
      else this.prev()
    } else {
      this.resume()
    }

    if (this.drag.moved) {
      requestAnimationFrame(() => {
        this.drag.moved = false
      })
    }
  }

  teardownDragListeners() {
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
    window.removeEventListener('pointercancel', this.onPointerUp)
  }

  goTo(index, direction = 'next') {
    if (!this.slides.length) return
    if (index < 0 || index >= this.slides.length) return
    if (index === this.currentIndex) {
      this.resetAutoplay()
      return
    }
    if (this.isAnimating) return

    this.isAnimating = true

    const previous = this.slides[this.currentIndex]
    const next = this.slides[index]
    const fromLeft = direction === 'prev'
    const exitingClass = fromLeft ? 'is-exiting-right' : 'is-exiting'

    next.classList.add('is-no-transition')
    next.classList.remove('is-exiting', 'is-exiting-right', 'is-active', 'is-from-left')
    if (fromLeft) next.classList.add('is-from-left')
    void next.offsetWidth
    next.classList.remove('is-no-transition')

    previous.classList.remove('is-active')
    previous.classList.add(exitingClass)
    previous.setAttribute('aria-hidden', 'true')
    previous.setAttribute('inert', '')

    next.classList.remove('is-from-left')
    next.classList.add('is-active')
    next.setAttribute('aria-hidden', 'false')
    next.removeAttribute('inert')

    let cleaned = false
    const cleanup = () => {
      if (cleaned) return
      cleaned = true
      if (this.cleanupTimer) {
        clearTimeout(this.cleanupTimer)
        this.cleanupTimer = null
      }
      previous.classList.add('is-no-transition')
      previous.classList.remove('is-exiting', 'is-exiting-right')
      void previous.offsetWidth
      previous.classList.remove('is-no-transition')
      this.isAnimating = false
    }

    const onExitEnd = (event) => {
      if (event.target !== previous || event.propertyName !== 'transform') return
      previous.removeEventListener('transitionend', onExitEnd)
      cleanup()
    }

    if (this.transitionSpeed === 0) {
      cleanup()
    } else {
      previous.addEventListener('transitionend', onExitEnd)
      this.cleanupTimer = setTimeout(cleanup, this.transitionSpeed + 50)
    }

    this.dots.forEach((dot, i) => {
      const selected = i === index
      dot.classList.toggle('is-active', selected)
      dot.setAttribute('aria-selected', selected ? 'true' : 'false')
    })

    this.currentIndex = index
    this.resetAutoplay()
  }

  next() {
    if (this.slides.length < 2) return
    this.goTo((this.currentIndex + 1) % this.slides.length, 'next')
  }

  prev() {
    if (this.slides.length < 2) return
    this.goTo((this.currentIndex - 1 + this.slides.length) % this.slides.length, 'prev')
  }

  startAutoplay() {
    this.stopAutoplay()
    if (!this.autoplayEnabled || this.slides.length < 2 || this.isPaused) return
    this.autoplayTimer = setInterval(this.next, this.autoplaySpeed)
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
  }

  resetAutoplay() {
    if (this.autoplayEnabled && this.slides.length > 1 && !this.isPaused) {
      this.startAutoplay()
    }
  }

  pause() {
    this.isPaused = true
    this.stopAutoplay()
  }

  resume() {
    if (document.hidden) return
    this.isPaused = false
    this.startAutoplay()
  }
}

if (!customElements.get('purchase-info-drawer')) {
  customElements.define('purchase-info-drawer', PurchaseInfoDrawer)
}

if (!customElements.get('purchase-info')) {
  customElements.define('purchase-info', PurchaseInfo)
}
